/**
 * Authentication Context
 *
 * Provides authentication state and methods to the entire app.
 * Also manages organization and product selection.
 */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../services/authService';
import { organizationService } from '../services/organizationService';
import { productService } from '../services/productService';
import { useTokenRefresh } from '../hooks/useTokenRefresh';
import { User } from '../types/auth';
import { Organization } from '../types/organization';
import { Product } from '../types/product';
import { api } from '../core/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  organizations: Organization[];
  currentOrganization: Organization | null;
  products: Product[];
  currentProduct: Product | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  setCurrentOrganization: (orgId: string) => void;
  setCurrentProduct: (productId: string) => void;
  refreshProducts: () => Promise<void>;
  patchCurrentOrganization: (patch: Partial<Organization>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrganization, setCurrentOrganizationState] = useState<Organization | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [currentProduct, setCurrentProductState] = useState<Product | null>(null);

  // Refresh token periodically while user is active (sliding session)
  useTokenRefresh();

  // Load user and organizations on mount
  useEffect(() => {
    const loadUser = async () => {
      if (authService.isAuthenticated()) {
        try {
          const currentUser = await authService.getCurrentUser();
          setUser(currentUser);

          // Load user's organizations
          await loadOrganizations();
        } catch (error) {
          console.error('Failed to load user:', error);
          authService.logout();
        }
      }
      setLoading(false);
    };

    loadUser();
  }, []);

  const loadOrganizations = async () => {
    try {
      const userOrgs = await organizationService.getMyOrganizations();
      setOrganizations(userOrgs);

      // Set current organization
      const savedOrgId = api.getCurrentOrganization();
      let orgToSet: Organization | null = null;

      if (savedOrgId) {
        // Try to find saved organization
        const foundOrg = userOrgs.find(org => org.id === savedOrgId);
        orgToSet = foundOrg || null;
      }

      // If no saved org or saved org not found, use first organization
      if (!orgToSet && userOrgs.length > 0) {
        orgToSet = userOrgs[0] || null;
      }

      if (orgToSet) {
        setCurrentOrganizationState(orgToSet);
        api.setCurrentOrganization(orgToSet.id);

        // Load products for this organization
        await loadProducts();
      }
    } catch (error) {
      console.error('Failed to load organizations:', error);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await productService.getProducts();
      const orgProducts = response.products.filter(p => p.is_active);
      setProducts(orgProducts);

      // Set current product
      const savedProductId = api.getCurrentProduct();
      let productToSet: Product | null = null;

      if (savedProductId) {
        // Try to find saved product
        const foundProduct = orgProducts.find(p => p.id === savedProductId);
        productToSet = foundProduct || null;
      }

      // If no saved product or saved product not found, use first product
      if (!productToSet && orgProducts.length > 0) {
        productToSet = orgProducts[0] || null;
      }

      if (productToSet) {
        setCurrentProductState(productToSet);
        api.setCurrentProduct(productToSet.id);
      } else {
        // No products available - clear current product
        setCurrentProductState(null);
        api.clearCurrentProduct();
      }
    } catch (error) {
      console.error('Failed to load products:', error);
    }
  };

  const login = async (email: string, password: string) => {
    const response = await authService.login({ email, password });
    setUser(response.user);

    // Load organizations after login
    await loadOrganizations();
  };

  const logout = () => {
    setUser(null);
    setOrganizations([]);
    setCurrentOrganizationState(null);
    setProducts([]);
    setCurrentProductState(null);
    api.clearCurrentOrganization(); // This also clears product
    authService.logout();
  };

  const refreshUser = async () => {
    if (authService.isAuthenticated()) {
      try {
        const currentUser = await authService.getCurrentUser();
        setUser(currentUser);
      } catch (error) {
        console.error('Failed to refresh user:', error);
        logout();
      }
    }
  };

  const setCurrentOrganization = async (orgId: string) => {
    const org = organizations.find(o => o.id === orgId);
    if (org) {
      setCurrentOrganizationState(org);
      api.setCurrentOrganization(orgId);

      // Clear current product and reload products for new organization
      setProducts([]);
      setCurrentProductState(null);
      api.clearCurrentProduct();

      // Load products for the new organization
      await loadProducts();

      // Reload data after organization change (if needed)
      // This allows components to re-fetch data for the new organization
      window.dispatchEvent(new CustomEvent('organizationChanged', { detail: { organizationId: orgId } }));
    }
  };

  const setCurrentProduct = (productId: string) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      setCurrentProductState(product);
      api.setCurrentProduct(productId);

      // Notify components about product change
      window.dispatchEvent(new CustomEvent('productChanged', { detail: { productId } }));
    }
  };

  const refreshProducts = async () => {
    await loadProducts();
  };

  /**
   * Patch the current organization in-memory (and its entry in the
   * organizations list, so the patch survives an org switch and back).
   * Lets callers reflect a server-side change (e.g. AI consent, DPA
   * acceptance) without a full page reload.
   */
  const patchCurrentOrganization = (patch: Partial<Organization>) => {
    // Capture the active org id once so both updates target the same row.
    // (Matching the list against `currentOrganization` from the closure while
    // the active-org updater uses `prev` let the two disagree on org switch.)
    const targetId = currentOrganization?.id;
    setCurrentOrganizationState(prev => (prev ? { ...prev, ...patch } : prev));
    if (!targetId) return;
    setOrganizations(prev =>
      prev.map(org => (org.id === targetId ? { ...org, ...patch } : org))
    );
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      organizations,
      currentOrganization,
      products,
      currentProduct,
      login,
      logout,
      refreshUser,
      setCurrentOrganization,
      setCurrentProduct,
      refreshProducts,
      patchCurrentOrganization
    }}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to use auth context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
